import { FileUp, Hash, Plus, Type, X } from "lucide-react"
import * as React from "react"
import { type Control, useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { PARAM_TYPES, type ParamType } from "@/api/queries/automations"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { slugifyParam } from "@/features/chat/templates"
import { z } from "@/lib/zod"

export const isParamType = (v: string): v is ParamType => (PARAM_TYPES as readonly string[]).includes(v)

export const paramSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(PARAM_TYPES),
  required: z.boolean(),
  default: z.string(),
})
export type ParamValues = z.infer<typeof paramSchema>

const TYPE_ICONS = { string: Type, number: Hash, file: FileUp } as const

/** Form slice the editor binds to (`parameter_schema` array). */
interface WithParams {
  parameter_schema: ParamValues[]
}

/**
 * Parameter list for an agent or team automation: label → auto-slugged name,
 * type, required and default. Validation (name format, duplicates) lives in
 * the form schema.
 */
export function ParamEditor() {
  const { t } = useTranslation()
  const form = useFormContext<WithParams>()
  const params = useFieldArray({ control: form.control, name: "parameter_schema" })
  const errors = form.formState.errors.parameter_schema as
    | { message?: string; root?: { message?: string } }
    | undefined
  const error = errors?.message ?? errors?.root?.message
  const add = () => params.append({ name: "", label: "", type: "string", required: false, default: "" })

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{t("chat.automations.params.title")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("chat.automations.params.description", { example: "{{ name }}" })}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="param-add">
          <Plus />
          {t("chat.automations.params.add")}
        </Button>
      </div>
      {params.fields.length === 0 ? (
        <button
          type="button"
          onClick={add}
          className="text-muted-foreground hover:bg-muted/40 w-full rounded-xl border border-dashed p-5 text-center text-sm transition-colors"
        >
          {t("chat.automations.params.empty")}
        </button>
      ) : (
        <div className="space-y-2">
          {params.fields.map((f, i) => (
            <ParamRow key={f.id} index={i} control={form.control} onRemove={() => params.remove(i)} />
          ))}
        </div>
      )}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </section>
  )
}

function ParamRow({
  index,
  control,
  onRemove,
}: {
  index: number
  control: Control<WithParams>
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const form = useFormContext<WithParams>()
  const id = React.useId()
  const base = `parameter_schema.${index}` as const
  const p = useWatch({ control, name: base })
  const nameTouched = React.useRef(!!p?.name)
  const err = form.formState.errors.parameter_schema?.[index]
  const type: ParamType = p?.type ?? "string"
  const Icon = TYPE_ICONS[type]
  const typeItems = PARAM_TYPES.map((x) => ({ value: x, label: t(`chat.automations.params.types.${x}`) }))
  const set = <K extends keyof ParamValues>(k: K, v: ParamValues[K], validate = false) =>
    form.setValue(`${base}.${k}`, v as never, {
      shouldDirty: true,
      shouldValidate: validate && form.formState.isSubmitted,
    })

  return (
    <div
      className="bg-card grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_8.5rem_auto]"
      data-testid="param-row"
    >
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs" htmlFor={`${id}-label`}>
          {t("chat.automations.params.label")}
        </label>
        <Input
          id={`${id}-label`}
          value={p?.label ?? ""}
          placeholder={t("chat.automations.params.labelPlaceholder")}
          onChange={(e) => {
            set("label", e.target.value)
            if (!nameTouched.current) set("name", slugifyParam(e.target.value), true)
          }}
        />
      </div>
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs" htmlFor={`${id}-name`}>
          {t("chat.automations.params.name")}
        </label>
        <Input
          id={`${id}-name`}
          value={p?.name ?? ""}
          placeholder="customer_name"
          aria-invalid={!!err?.name || undefined}
          className="font-mono text-[0.8125rem]"
          onChange={(e) => {
            nameTouched.current = true
            set("name", e.target.value.replace(/\s+/g, "_"), true)
          }}
        />
        {err?.name?.message && <p className="text-destructive text-xs">{err.name.message}</p>}
      </div>
      <div className="space-y-1">
        <span className="text-muted-foreground text-xs">{t("chat.automations.params.type")}</span>
        <Select items={typeItems} value={type} onValueChange={(v) => v && isParamType(v) && set("type", v)}>
          <SelectTrigger className="w-full" aria-label={t("chat.automations.params.type")}>
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <Icon className="text-muted-foreground size-3.5" />
                {t(`chat.automations.params.types.${type}`)}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {typeItems.map((x) => {
              const I = TYPE_ICONS[x.value]
              return (
                <SelectItem key={x.value} value={x.value}>
                  <I className="text-muted-foreground size-3.5" />
                  {x.label}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={onRemove}
          aria-label={t("chat.automations.params.remove")}
        >
          <X />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:col-span-4">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Base UI's Checkbox renders the control inside */}
        <label className="flex items-center gap-2 text-[0.8125rem]">
          <Checkbox checked={!!p?.required} onCheckedChange={(c) => set("required", !!c)} />
          {t("chat.automations.params.required")}
        </label>
        {type !== "file" ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <label className="text-muted-foreground shrink-0 text-xs" htmlFor={`${id}-default`}>
              {t("chat.automations.params.default")}
            </label>
            <Input
              id={`${id}-default`}
              value={p?.default ?? ""}
              inputMode={type === "number" ? "decimal" : undefined}
              placeholder={t("chat.automations.params.noDefault")}
              className="h-7 max-w-64 text-[0.8125rem]"
              onChange={(e) => set("default", e.target.value)}
            />
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">{t("chat.automations.params.fileHint")}</span>
        )}
      </div>
    </div>
  )
}
